import React, { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";

import { useToast } from "hooks/toast";
import {
  listKitArquivos,
  downloadKitArquivo,
  downloadKitZip,
} from "services/onboardingApi";
import { downloadBlob } from "utils/downloadBlob";
import { formatBytes } from "utils/formatBytes";
import { formatDateTime } from "utils/formatDate";

// Erro de chamada com responseType blob vem como Blob; lê o JSON de dentro
// para mostrar a mensagem do backend em vez de "[object Blob]".
async function lerMensagemErro(err) {
  const data = err && err.response && err.response.data;
  try {
    if (data && typeof data.text === "function") {
      const json = JSON.parse(await data.text());
      return json.error || json.message || err.message;
    }
    if (data && data.error) return data.error;
  } catch (e) {
    /* cai no genérico */
  }
  return err.message || "Erro desconhecido";
}

// Arquivos recebidos pelo kit de onboarding (PO1-2896): lista por campo o que
// o cliente enviou pelo link público e permite baixar um a um ou tudo em .zip.
// Só listagem e download (sem viewer). Isolado do resto do checklist: falha de
// SFTP vira mensagem aqui dentro, o checklist continua utilizável. Funciona
// também para onboarding concluído (Status=1).
export default function KitArquivosSection({ idEmbarcador, apelido }) {
  const { addToast } = useToast();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [baixando, setBaixando] = useState(null); // nome do arquivo ou "zip"

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const r = await listKitArquivos(idEmbarcador);
      setDados(r);
    } catch (err) {
      setDados(null);
      setErro(await lerMensagemErro(err));
    } finally {
      setLoading(false);
    }
  }, [idEmbarcador]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const baixar = useCallback(
    async (campo, nome) => {
      setBaixando(nome);
      try {
        const blob = await downloadKitArquivo(idEmbarcador, campo, nome);
        downloadBlob(blob, nome);
      } catch (err) {
        const msg = await lerMensagemErro(err);
        if (err.response && err.response.status === 404) {
          // Wing FTP já listou arquivo que não existe mais (PO1-2907): avisa
          // e recarrega a lista para refletir o estado real.
          addToast({
            place: "tr",
            type: "warning",
            title: "Arquivo não encontrado no SFTP",
            description: "A listagem pode estar defasada; atualizamos a seção.",
            icon: "tim-icons icon-alert-circle-exc",
          });
          carregar();
        } else {
          addToast({
            place: "tr",
            type: "danger",
            title: "Erro ao baixar arquivo",
            description: msg,
            icon: "tim-icons icon-alert-circle-exc",
          });
        }
      } finally {
        setBaixando(null);
      }
    },
    [idEmbarcador, addToast, carregar]
  );

  const baixarTudo = useCallback(async () => {
    setBaixando("zip");
    try {
      const blob = await downloadKitZip(idEmbarcador);
      const hoje = new Date();
      const aaaammdd = `${hoje.getFullYear()}${String(hoje.getMonth() + 1).padStart(2, "0")}${String(
        hoje.getDate()
      ).padStart(2, "0")}`;
      downloadBlob(blob, `onboarding-${apelido || idEmbarcador}-${aaaammdd}.zip`);
    } catch (err) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao baixar o .zip",
        description: await lerMensagemErro(err),
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setBaixando(null);
    }
  }, [idEmbarcador, apelido, addToast]);

  const total = dados ? dados.total : 0;

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <p style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 0 }}>
            Arquivos recebidos pelo kit
          </p>
          {dados && (
            <small style={{ color: "#9a9a9a" }}>
              SFTP · {dados.pasta} · {total} arquivo(s)
            </small>
          )}
        </div>
        <Button
          color="info"
          size="sm"
          outline
          onClick={baixarTudo}
          disabled={loading || !dados || total === 0 || Boolean(baixando)}
          title={total === 0 ? "Nenhum arquivo recebido ainda" : "Baixa as 3 pastas em um .zip"}
        >
          {baixando === "zip" ? <Spinner size="sm" /> : "Baixar tudo (.zip)"}
        </Button>
      </div>

      {loading && (
        <div className="text-center mt-2 mb-2">
          <Spinner size="sm" />
        </div>
      )}

      {!loading && erro && (
        <div className="onb-info" style={{ fontSize: 12 }}>
          Não foi possível consultar o SFTP agora. {erro}
          <div className="mt-2">
            <Button color="secondary" size="sm" outline onClick={carregar}>
              Tentar de novo
            </Button>
          </div>
        </div>
      )}

      {!loading &&
        dados &&
        dados.campos.map((grupo) => (
          <div
            key={grupo.campo}
            className="onb-filial"
            style={{ alignItems: "flex-start", flexDirection: "column", gap: 6 }}
          >
            <div className="d-flex justify-content-between w-100">
              <div className="nm">{grupo.rotulo}</div>
              <div className="ci">{grupo.arquivos.length} arquivo(s)</div>
            </div>
            {grupo.arquivos.length === 0 ? (
              <div className="ci">Nenhum arquivo recebido ainda</div>
            ) : (
              <div className="w-100">
                {grupo.arquivos.map((arq) => (
                  <div
                    key={arq.nome}
                    className="d-flex justify-content-between align-items-center"
                    style={{ padding: "4px 0", gap: 10 }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "var(--everlog-card-title, #ffffff)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={arq.nome}
                      >
                        {arq.nome}
                      </div>
                      <div className="ci">
                        {formatBytes(arq.tamanho)}
                        {arq.modificadoEm ? ` · ${formatDateTime(arq.modificadoEm)}` : ""}
                      </div>
                    </div>
                    <Button
                      color="info"
                      size="sm"
                      outline
                      style={{ whiteSpace: "nowrap" }}
                      disabled={Boolean(baixando)}
                      onClick={() => baixar(grupo.campo, arq.nome)}
                    >
                      {baixando === arq.nome ? <Spinner size="sm" /> : "Baixar"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}
